import { v4 as uuidv4 } from 'uuid';
import { axiosInstance } from 'services/http';

export const ACCOUNTING_REPORT_RESOURCE = 'prod-pay-master-sheet-quickbooks';

const PAY_PERIOD_START_PARAM = 'payPeriodStartDay';
const PAY_PERIOD_END_PARAM = 'payPeriodEndDay';

const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The calendar-day range the user picked, as plain YYYY-MM-DD. */
export interface PayPeriod {
  startDay: string;
  endDay: string;
}

export interface AccountingReportFile {
  id: string;
  blob: Blob;
}

/**
 * The Pay Period rides along on its own params because the report prints the
 * picked days verbatim (DATE and REF NO.) and the row filters in `query` are
 * zone-shifted instants that cannot tell us the picked day.
 */
export const downloadAccountingReport = (
  query: string,
  payPeriod?: PayPeriod,
): Promise<{ data: AccountingReportFile }> => {
  const params = new URLSearchParams(query);
  if (payPeriod) {
    params.set(PAY_PERIOD_START_PARAM, payPeriod.startDay);
    params.set(PAY_PERIOD_END_PARAM, payPeriod.endDay);
  }

  return axiosInstance
    .get(`/${ACCOUNTING_REPORT_RESOURCE}?${params}`, {
      responseType: 'arraybuffer',
      headers: { 'Content-Type': XLSX_MIME_TYPE },
    })
    .then(response => ({
      data: {
        id: uuidv4(),
        blob: new Blob([response.data], { type: XLSX_MIME_TYPE }),
      },
    }));
};

const EXPORT_FAILED_MESSAGE = 'Accounting Export failed. Please try again.';

/**
 * The export is requested as an `arraybuffer`, so when the API rejects it the
 * Nest error body arrives as bytes, not as the parsed JSON the rest of the
 * data provider reads `message` from.
 */
const isArrayBuffer = (data: unknown): data is ArrayBuffer =>
  Object.prototype.toString.call(data) === '[object ArrayBuffer]';

const decodeErrorBody = (data: unknown): { message?: unknown } | undefined => {
  if (isArrayBuffer(data)) {
    try {
      return JSON.parse(new TextDecoder().decode(data));
    } catch {
      return undefined;
    }
  }
  return typeof data === 'object' && data !== null
    ? (data as { message?: unknown })
    : undefined;
};

export const accountingReportErrorMessage = (error: unknown): string => {
  const response = (
    error as { response?: { data?: unknown; statusText?: string } }
  )?.response;
  const body = decodeErrorBody(response?.data);
  const message = Array.isArray(body?.message)
    ? body?.message[0]
    : body?.message;

  if (typeof message === 'string' && message) {
    return message;
  }
  return (
    response?.statusText ||
    (error as { message?: string })?.message ||
    EXPORT_FAILED_MESSAGE
  );
};
